import { ChangeDetectorRef, Component, OnInit, ViewChild, OnDestroy, Input, Output, EventEmitter, NgZone } from '@angular/core';
import { ActionSheetController, IonInput, IonModal, IonPopover, IonSearchbar, Platform } from '@ionic/angular';
import { MinimalProjectInfo, Project, ProjectFolder, ProjectStatus } from '../project.model';
import { SettingsService } from '../../settings/settings.service';
import { ProjectService } from '../project.service';
import { SyncService } from '../../core/sync/sync.service';
import { DbService } from '../../core/db/db.service';
import { AuthService } from '../../auth/auth.service';
import { LoggerService } from '../../core/logger/logger.service';
import { UtilsService } from '../../core/utils/utils.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../../core/notification/notification.service';
import { Router } from '@angular/router';
import { lastValueFrom, ReplaySubject, Subscription } from 'rxjs';
import { SyncInfoInterface } from '../../core/sync/sync-info.interface';
import { Address } from '../../core/shared-models/address.model';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { DocumentClient } from 'aws-sdk/lib/dynamodb/document_client';
import QueryInput = DocumentClient.QueryInput;
import { ProductFolder } from '../../products/product-folder.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { StorageService } from '../../core/storage/storage.service';

@Component({
    selector: 'app-project-list',
    templateUrl: './project-list.component.html',
    styleUrls: ['./project-list.component.scss'],
})
export class ProjectListComponent implements OnInit, OnDestroy {
    currentVisibleProjects: Project[] = [];
    currentVisibleProjectFolders: ProjectFolder[] = [];
    combinedListItems: Array<Project | ProjectFolder> = [];
    currentFolderPath: Array<ProjectFolder> = null; // the current folder path in order

    currentDraggedItems: Array<Project | ProjectFolder> = []; // set when drag starts, to know which items we need to move on drop

    chooseFolderMoveTargetModalVisible = false;
    currentlyMovingItems: Array<Project | ProjectFolder> = []; // used to manually move items to target folder

    duplicateModalVisible = false;
    copyProject = true;
    copyJobs = false;
    projectToDuplicate: Project;
    copyLoading = false;

    dbChangefeedSubscription: Subscription;

    // search
    searchActive = false;
    folderSearchResults: ProjectFolder[] = [];
    projectSearchResults: Project[] = [];
    @ViewChild('projectSearchBarInput') projectSearchBarInput: IonSearchbar;
    private _projectListScroll: CdkVirtualScrollViewport;

    @ViewChild('projectListScroll')
    set projectListScroll(value: CdkVirtualScrollViewport) {
        const firstInit = this._projectListScroll == null && value != null;
        if (value === null && this._projectListScroll != null) {
            // remove all dangling observers
            this.cdkViewPortObserver.disconnect();
        }
        this._projectListScroll = value;
        if (firstInit) {
            this.logger.log('[PROJECT LIST] Observing project list scroll for resize events');
            if (this.cdkViewPortObserver == null) {
                this.cdkViewPortObserver = new ResizeObserver(() => {
                    // Angular has not "monkey patched" the js event of ResizeObserver
                    // so we need to manually run it in the zone to trigger Change detection
                    this.zone.run(() => {
                        this.logger.log('[PROJECT LIST] CDK Viewport changed size');
                        this._projectListScroll?.checkViewportSize();
                    });
                });
            }
            this.cdkViewPortObserver.observe(value.elementRef.nativeElement);
        }
    }

    get projectListScroll(): CdkVirtualScrollViewport {
        return this._projectListScroll;
    }

    // folder editing
    currentlyEditingFolder: ProjectFolder = null;
    currentlyCreatingNewFolder = false;
    editFolderName = '';
    @ViewChild('editFolderPopover') editFolderPopover: IonPopover;
    @ViewChild('editFolderNameInput') editFolderNameInput: IonInput;

    @Input() mode: 'manage' | 'select' | 'select-folder' | 'multi-select' = 'manage';
    @Input() selectedProjectIds: string[] = [];
    currentSelectedProject = null;
    currentSelectedProjectIds: Set<string> = new Set();
    @Output() projectSelectionChanged = new EventEmitter<Project>();
    @Output() folderSelectionChanged = new EventEmitter<ProjectFolder>();
    @Output() multiProjectSelectionConfirmed = new EventEmitter<string[]>();

    syncInfoSubscription: Subscription;
    cdkViewPortObserver: ResizeObserver;

    // support
    isDokuPitSupportAccount = false;
    supportProjectListModalVisible = false;
    supportProjectList: MinimalProjectInfo[] = [];
    visibleSupportProjectList: MinimalProjectInfo[] = [];
    supportProjectLoading = false;

    // helper variables
    managementChangesAllowed = false;
    currentProjectListSortingMode: 'name-a-z' | 'name-z-a' | 'project-number-1-9' | 'project-number-9-1' = 'name-a-z';
    syncInfo: SyncInfoInterface = null;
    jobSearchModalVisible = false;
    lastJobSearchString = null; // the last job search string (from the code scanner)
    lastJobSearchResult = null; // the result of the last job search
    jobSearchLoading = false;

    @ViewChild('supportProjectSearchbar') supportProjectSearchbar: IonSearchbar;
    loading = false;

    private destroyed$: ReplaySubject<boolean>;
    scanSearchEnabled = false;

    longPressTimer: any;

    mapViewEnabled = false;

    constructor(
        protected projSrv: ProjectService,
        private storage: StorageService,
        private syncSrv: SyncService,
        private db: DbService,
        protected authSrv: AuthService,
        private logger: LoggerService,
        public settingsSrv: SettingsService,
        public utils: UtilsService,
        public translateService: TranslateService,
        private notifySrv: NotificationService,
        protected platform: Platform,
        private router: Router,
        private http: HttpClient,
        private cdr: ChangeDetectorRef,
        private zone: NgZone,
        private actionSheetCtrl: ActionSheetController
    ) {
        this.destroyed$ = new ReplaySubject<boolean>(1);

        this.platform.backButton.subscribeWithPriority(10, () => {
            // only ever do one navigation - dont want to close the scanner and go back in folder hierarchy
            if (this.scanSearchEnabled) {
                this.scanSearchEnabled = false;
            }
        });
    }

    async ngOnInit() {
        this.loading = true;
        this.searchActive = false;
        if (this.projectSearchBarInput) {
            this.projectSearchBarInput.value = '';
        }

        if (this.mode === 'multi-select' && this.selectedProjectIds?.length > 0) {
            this.currentSelectedProjectIds = new Set(this.selectedProjectIds);
        }

        this.syncInfoSubscription = this.syncSrv.syncInfo$.pipe(takeUntil(this.destroyed$)).subscribe((syncInfo) => {
            this.syncInfo = syncInfo;
        });

        this.isDokuPitSupportAccount = this.authSrv.authInfo.userData?.dokupitSupportAccount;

        this.managementChangesAllowed = this.settingsSrv.currentUserRights.canManageProjects;

        await this.loadInitialView();
    }

    ngOnDestroy() {
        // trigger unsub of all subscriptions
        this.destroyed$.next(true);

        // its possible for the projectListScroll to be null if the component is destroyed before the view is fully initialized
        // or the list is empty so we dont have to render it at all
        if (this.projectListScroll) {
            this.cdkViewPortObserver.unobserve(this.projectListScroll.elementRef.nativeElement);
        }
        this.scanSearchEnabled = false;
    }

    /**
     * Loads initial view
     */
    async loadInitialView() {
        this.currentProjectListSortingMode = await this.storage.get('dp_project_list_sorting_mode');
        this.currentFolderPath = null;
        if (this.searchActive) {
            await this.initSearchResultsView();
        } else {
            // we are not limiting display so we load base view
            await this.displayProjectsBasedOnFolder();

            if (this.dbChangefeedSubscription == null) {
                this.dbChangefeedSubscription = this.db.itemsChanged$.pipe(takeUntil(this.destroyed$)).subscribe((changes) => {
                    changes = changes.filter((x) => x.item.type === 'project' || x.item.type === 'project-folder');
                    if (changes.length > 0) {
                        this.displayProjectsBasedOnFolder(); // refresh view if products changed
                    }
                    let folderDeleted = changes.find((x) => x.operation === 'DELETE' && x.item.type === 'project-folder');
                    if (folderDeleted != null) {
                        // at least one folder was deleted, so we start a downstream sync because maybe it included projects which were moved to base path by server
                        this.syncSrv.syncDownstream();
                    }
                });
            }
        }
    }

    /**
     * Loads products based on current folder path
     */
    async displayProjectsBasedOnFolder() {
        this.loading = true;
        this.currentVisibleProjects = [];
        this.currentVisibleProjectFolders = [];
        this.combinedListItems = [];

        let folderIdToSearch;
        if (this.currentFolderPath == null) {
            folderIdToSearch = null;
        } else {
            folderIdToSearch = this.currentFolderPath[this.currentFolderPath.length - 1]._id;
        }

        if (!this.searchActive || (this.searchActive && this.currentFolderPath != null)) {
            // default handling
            if (this.mode !== 'select-folder') {
                try {
                    this.currentVisibleProjects = await this.projSrv.getProjectsOfFolder(folderIdToSearch);
                    await this.sortProjectList(this.currentVisibleProjects);
                } catch (folderLoadErr) {
                    this.logger.error('[PROJECT LIST] could not load project', folderLoadErr);
                    this.notifySrv.error(this.translateService.instant('dashboard.projects.info.project-list-load-err'));
                }
            }
            try {
                this.currentVisibleProjectFolders = await this.projSrv.getFoldersOfFolder(folderIdToSearch);
                await this.sortProjectList(this.currentVisibleProjectFolders);
            } catch (folderLoadErr) {
                this.logger.error('[PROJECT LIST] could not load project folders', folderLoadErr);
                this.notifySrv.error(this.translateService.instant('dashboard.projects.info.project-list-load-err'));
            }

            this.updateCombinedList();
            this.projectListScroll?.checkViewportSize();
        } else {
            // search is active and folder path is null -> so we have to adjust handling if we are on base path, we dont want to show actual base products/folders but the search results again, so we handle search again to refresh
            await this.initSearchResultsView();
        }

        this.loading = false;
        this.cdr.detectChanges();
    }

    onProjectLongPress($event, item, anchor) {
        if (this.managementChangesAllowed && this.platform.is('ios') && this.mode === 'manage') {
            this.longPressTimer = setTimeout(async () => {
                let actionSheetOptions = {
                    header: this.translateService.instant('dashboard.projects.project-options'),
                    buttons: [
                        {
                            text: this.translateService.instant('dashboard.projects.move-to-folder'),
                            handler: () => {
                                this.startManualItemToFolderMove(item);
                            },
                        },
                        {
                            text: this.translateService.instant('dashboard.projects.remove-from-folder'),
                            handler: () => {
                                this.onMoveItemToBase(item);
                            },
                        },
                        {
                            text: this.translateService.instant('dashboard.projects.toolbar.add-folder'),
                            handler: () => {
                                this.onCreateNewFolder(anchor);
                            },
                        },
                        {
                            text: this.translateService.instant('dashboard.projects.duplicate-project'),
                            handler: () => {
                                this.onStartDuplicatingProject(item);
                            },
                        },
                        {
                            text: this.translateService.instant('dashboard.projects.archive-project'),
                            handler: () => {
                                this.onArchiveProject(item);
                            },
                        },
                        {
                            text: this.translateService.instant('dashboard.projects.delete-project'),
                            role: 'destructive',
                            handler: () => {
                                this.onDeleteProject(item);
                            },
                        },
                        {
                            text: this.translateService.instant('shared.cancel'),
                            role: 'cancel',
                        },
                    ],
                };

                if (item.projectStatus != this.possibleProjectStatus.INPROGRESS) {
                    actionSheetOptions.buttons.splice(3, 3);
                }
                const actionSheet = await this.actionSheetCtrl.create(actionSheetOptions);
                await actionSheet.present();
            }, 500);
        }
    }

    onFolderLongPress($event, item, anchor) {
        if (this.managementChangesAllowed && this.platform.is('ios') && this.mode === 'manage') {
            this.longPressTimer = setTimeout(async () => {
                let actionSheetOptions = {
                    header: this.translateService.instant('dashboard.projects.folder-options'),
                    buttons: [
                        {
                            text: this.translateService.instant('dashboard.projects.move-to-folder'),
                            handler: () => {
                                this.startManualItemToFolderMove(item);
                            },
                        },
                        {
                            text: this.translateService.instant('dashboard.projects.remove-from-folder'),
                            handler: () => {
                                this.onMoveItemToBase(item);
                            },
                        },
                        {
                            text: this.translateService.instant('dashboard.projects.toolbar.add-folder'),
                            handler: () => {
                                this.onCreateNewFolder(anchor);
                            },
                        },
                        {
                            text: this.translateService.instant('projects.info.folder.rename-folder'),
                            handler: () => {
                                this.onEditFolder(item, anchor);
                            },
                        },
                        {
                            text: this.translateService.instant('projects.info.folder.delete-folder'),
                            role: 'destructive',
                            handler: () => {
                                this.onDeleteFolder(item);
                            },
                        },
                        {
                            text: this.translateService.instant('shared.cancel'),
                            role: 'cancel',
                        },
                    ],
                };

                const actionSheet = await this.actionSheetCtrl.create(actionSheetOptions);
                await actionSheet.present();
            }, 500);
        }
    }

    onLongPressEnd($event) {
        if (this.platform.is('ios')) {
            clearTimeout(this.longPressTimer);
        }
    }

    /**
     * Used to load initial view of search results
     */
    async initSearchResultsView() {
        // we load search-result folders and products and display them, afterwards default folder navigation kicks in
        this.currentFolderPath = null;
        this.currentVisibleProjects = this.projectSearchResults;
        this.currentVisibleProjectFolders = this.folderSearchResults;
        await this.sortProjectList(this.currentVisibleProjects);
        await this.sortProjectList(this.currentVisibleProjectFolders);
        this.updateCombinedList();
        this.loading = false;
    }

    /**
     * Triggered when a folder is clicked in breadcrumb
     * @param pathIndex
     */
    navigateToFolderViaBreadcrumb(pathIndex: number) {
        this.currentFolderPath = this.currentFolderPath.slice(0, pathIndex + 1);
        this.displayProjectsBasedOnFolder();
    }

    /**
     * Triggered when a folder is clicked
     */
    onFolderClicked(folder: ProjectFolder) {
        if (this.currentFolderPath == null) {
            this.currentFolderPath = [folder];
        } else {
            this.currentFolderPath.push(folder);
        }
        this.displayProjectsBasedOnFolder();
    }

    /**
     * Triggered when a project is clicked
     * @param project
     */
    onProjectClicked(project: Project) {
        switch (this.mode) {
            case 'manage': {
                this.router.navigate(['/project', project._id, 'jobs']);
                break;
            }
            case 'select': {
                this.currentSelectedProject = project;
                this.projectSelectionChanged.emit(this.currentSelectedProject);
                break;
            }
            case 'multi-select': {
                if (this.currentSelectedProjectIds.has(project._id)) {
                    this.currentSelectedProjectIds.delete(project._id);
                } else {
                    this.currentSelectedProjectIds.add(project._id);
                }
                break;
            }
            default: {
                break;
            }
        }
    }

    /**
     * Called if user clicks on the confirm button (only used in mode "multi-select")
     */
    onConfirmMultiSelection() {
        this.multiProjectSelectionConfirmed.emit(Array.from(this.currentSelectedProjectIds));
    }

    /**
     * Check if project is selected
     * @param projectId the project id
     */
    isProjectSelected(projectId: string): boolean {
        return this.currentSelectedProjectIds.has(projectId);
    }

    /**
     * Starts manual item moval process by displaying modal to select target folder
     * @param item
     */
    startManualItemToFolderMove(item: Project | ProjectFolder) {
        this.currentlyMovingItems.push(item);
        this.chooseFolderMoveTargetModalVisible = true;
    }

    /**
     * Finishes manual item moval process by saving the new folder to moved projects
     * @param targetFolder
     */
    async finishManualItemToFolderMove(targetFolder: ProjectFolder) {
        if (this.mode !== 'manage') {
            // drop not allowed in these modes, reset and return
            this.currentlyMovingItems = [];
            return;
        }

        this.chooseFolderMoveTargetModalVisible = false;
        this.logger.log('[PROJECTS PAGE] manual moving items to new target folder', this.currentlyMovingItems, targetFolder);
        this.notifySrv.info(this.translateService.instant('dashboard.projects.info.projects-moving-info'), null, true, null, false);
        for (let item of this.currentlyMovingItems) {
            if (item.type === 'project') {
                (item as Project).parentFolder = targetFolder != null ? targetFolder._id : null; // change folder on project
                await lastValueFrom(this.db.put(item));
            } else if (item.type === 'project-folder') {
                // dont allow dropping a folder on itself
                if (targetFolder == null || targetFolder._id !== item._id) {
                    (item as ProductFolder).parentFolder = targetFolder != null ? targetFolder._id : null; // change parentFolder on folder
                    await lastValueFrom(this.db.put(item));
                }
            }
        }
        setTimeout(() => {
            this.notifySrv.closeCurrentSweetAlert();
        }, 1000);
        this.currentlyMovingItems = []; // reset
        await this.displayProjectsBasedOnFolder(); // refresh our view
    }

    /**
     * Triggered when a folder is selected in folder-select-mode
     * @param folder
     */
    onFolderSelectedAsMoveTarget(folder: ProjectFolder) {
        this.folderSelectionChanged.emit(folder);
    }

    /**
     * Triggered when a drag starts on project or folder
     * @param item
     */
    onStartDragItem(item: Project | ProjectFolder) {
        this.currentDraggedItems = [item];
    }

    /**
     * Triggered when the user drops the dragged item on a folder. Saves the new folder to moved projects
     * @param targetFolder
     */
    async onDropItemOnFolder(targetFolder: ProjectFolder) {
        if (this.mode !== 'manage') {
            // drop not allowed in these modes, reset and return
            this.currentDraggedItems = [];
            return;
        }
        this.logger.log('[PROJECTS PAGE] moving items to new target folder', this.currentDraggedItems, targetFolder);
        this.notifySrv.info(this.translateService.instant('dashboard.projects.info.projects-moving-info'), null, true, null, false);
        for (let item of this.currentDraggedItems) {
            if (item.type === 'project') {
                (item as Project).parentFolder = targetFolder != null ? targetFolder._id : null; // change folder on project
                await lastValueFrom(this.db.put(item));
            } else if (item.type === 'project-folder') {
                // dont allow dropping a folder on itself
                if (targetFolder == null || targetFolder._id !== item._id) {
                    (item as ProductFolder).parentFolder = targetFolder != null ? targetFolder._id : null; // change parentFolder on folder
                    await lastValueFrom(this.db.put(item));
                }
            }
        }
        setTimeout(() => {
            this.notifySrv.closeCurrentSweetAlert();
        }, 1000);
        this.currentDraggedItems = []; // reset
        await this.displayProjectsBasedOnFolder(); // refresh our view
    }

    /**
     * Toggles search mode
     */
    toggleSearch(newStatus?: boolean) {
        if (newStatus != null) {
            this.searchActive = newStatus;
        } else {
            this.searchActive = !this.searchActive;
        }
        if (this.searchActive) {
            this.cdr.detectChanges(); // force *ngIf to render the searchbar synchronously
            setTimeout(async () => {
                await this.projectSearchBarInput?.setFocus();
            }, 150);
        } else {
            this.loadInitialView();
        }
    }

    async handleSearch(data) {
        let searchString = data.detail.value;
        if (searchString == '') {
            this.toggleSearch(false); // end search if search string gets deleted
            return;
        }
        let searchStringRegexpString = '.*' + searchString + '.*';
        let searchStringRegexp = new RegExp(searchStringRegexpString, 'i'); // case insensitive regex

        // search products and folders
        this.loading = true;
        let foundProjectFolders: Array<ProjectFolder> = [];
        let foundProjects: Array<Project> = [];

        try {
            if (!this.utils.isNative) {
                // regex search is not working on native devices running sqlite, so we only use regex search in web and manually match on native devices
                let queryRes = await this.db.dbItems
                    .find({
                        selector: {
                            $and: [
                                {
                                    $or: [
                                        { name: { $regex: searchStringRegexpString, $options: 'i' } },
                                        { projectNumber: { $regex: searchStringRegexpString, $options: 'i' } },
                                    ],
                                },
                                {
                                    $or: [{ type: { $eq: 'project' } }, { type: { $eq: 'project-folder' } }],
                                },
                            ],
                        },
                        sort: [{ name: 'desc' }],
                        index: 'type', // for performance optimization tell rxdb which index to use
                    })
                    .exec();
                if (Array.isArray(queryRes)) {
                    for (let entry of queryRes) {
                        entry = entry.toJSON();
                        if (entry.type === 'project') {
                            foundProjects.push(new Project(entry));
                        } else if (entry.type === 'project-folder') {
                            foundProjectFolders.push(new ProjectFolder(entry));
                        }
                    }
                }
            } else {
                let queryRes = await this.db.dbItems
                    .find({
                        selector: {
                            $or: [{ type: { $eq: 'project' } }, { type: { $eq: 'project-folder' } }],
                        },
                        sort: [{ name: 'desc' }],
                        index: 'type', // for performance optimization tell rxdb which index to use
                    })
                    .exec();
                for (let entry of queryRes) {
                    entry = entry.toJSON();
                    if (entry.name?.match(searchStringRegexp) != null || entry.projectNumber?.match(searchStringRegexp) != null) {
                        if (entry.type === 'project') {
                            foundProjects.push(new Project(entry));
                        } else if (entry.type === 'project-folder') {
                            foundProjectFolders.push(new ProjectFolder(entry));
                        }
                    }
                }
            }
            this.folderSearchResults = foundProjectFolders;
            if (this.mode !== 'select-folder') {
                this.projectSearchResults = foundProjects;
            } else {
                this.projectSearchResults = [];
            }
            await this.initSearchResultsView();
        } catch (dbErr) {
            this.logger.log('[PROJECT LIST] project search failed', dbErr);
            this.loading = false;
        }
    }

    /**
     * Shows popover to create new folder
     * @param triggerElement the element with the click event to position the popover
     */
    async onCreateNewFolder(triggerElement: any) {
        let triggerElementRect = triggerElement.el.getBoundingClientRect();
        const fakeEvent = { target: triggerElement.el, clientX: triggerElementRect.left, clientY: triggerElementRect.top } as any;
        this.currentlyEditingFolder = new ProjectFolder();
        this.currentlyCreatingNewFolder = true;
        this.currentlyEditingFolder.relation = 'company#' + this.authSrv.authInfo.companyData._id;

        if (this.currentFolderPath?.length > 0) {
            this.currentlyEditingFolder.parentFolder = this.currentFolderPath[this.currentFolderPath.length - 1]._id; // save id of current folder as parent
        }

        this.editFolderName = 'Neuer Ordner';
        await this.editFolderPopover.present(fakeEvent);
        this.editFolderNameInput.setFocus();
    }

    /**
     * Shows popover to edit existing folder
     * @param folder
     * @param triggerElement the element with the click event to position the popover
     */
    async onEditFolder(folder: ProjectFolder, triggerElement: any) {
        let triggerElementRect = triggerElement.el.getBoundingClientRect();
        const fakeEvent = { target: triggerElement.el, clientX: triggerElementRect.left, clientY: triggerElementRect.top } as any;
        this.currentlyEditingFolder = folder;
        this.currentlyCreatingNewFolder = false;
        this.editFolderName = folder.name;
        await this.editFolderPopover.present(fakeEvent);
        this.editFolderNameInput.setFocus();
    }

    /**
     * Triggered when create/edit folder is clicked in popover
     */
    async onConfirmCurrentFolderEdit() {
        this.currentlyEditingFolder.name = this.editFolderName;
        await this.db.put(this.currentlyEditingFolder).toPromise();
        await this.editFolderPopover.dismiss();
    }

    /**
     * triggered when delete folder button is clicked
     * @param fol
     */
    onDeleteFolder(fol: ProjectFolder) {
        this.notifySrv
            .confirm(
                this.translateService.instant('dashboard.projects.folder.delete-folder-text'),
                this.translateService.instant('dashboard.projects.folder.delete-folder-heading')
            )
            .then(async () => {
                this.notifySrv.info(this.translateService.instant('projects.info.folder.deleting-folder'), null, true);
                // confirmed, call api to delete the folder
                this.http
                    .post(
                        environment.AWS_API_URL + '/project/delete-folder',
                        JSON.stringify({ folder: fol, username: this.authSrv.authInfo.userData.userName })
                    )
                    .subscribe(
                        (res) => {
                            this.notifySrv.closeCurrentSweetAlert();
                        },
                        (err) => {
                            this.logger.error('[PROJECT LIST] could not delete folder', err);
                            this.notifySrv.error(
                                this.translateService.instant('projects.info.folder.deleting-folder-error'),
                                null,
                                null,
                                true
                            );
                        }
                    );
            })
            .catch(() => {});
    }

    /**
     * Moves given project to root
     * @param item
     */
    async onMoveItemToBase(item: Project | ProjectFolder) {
        if (item.parentFolder == null) {
            this.notifySrv.info(this.translateService.instant('dashboard.projects.folder.project-folder-is-null'), null, true, 2000);
            return;
        }
        this.notifySrv.success(this.translateService.instant('dashboard.projects.folder.project-folder-set-to-null'), null, true, 2000);
        item.parentFolder = null;
        await lastValueFrom(this.db.put(item));
    }

    /**
     * Shows a list of ALL available projects in this company (used for support)
     * @private
     */
    async showDokuPitSupportProjectList() {
        if (!this.isDokuPitSupportAccount) {
            this.logger.warn('[DASHBOARD] no dokupit support user');
            return;
        }
        this.supportProjectLoading = true;
        this.supportProjectListModalVisible = true;
        // download project list for dokupit support team users (whole project list)
        this.logger.log('[SYNC] downloading project list for dokupit support team user');

        setTimeout((_) => {
            this.supportProjectSearchbar.setFocus();
        }, 400);

        // get project share items for company
        let supportProjectListQueryComplete = false; // we will send queries until we have all items (pagination)
        let supportProjectListLastEvaluatedKey = null; // we get this from dynamodb if the result doesnt include all items (pagination)
        while (supportProjectListQueryComplete == false) {
            try {
                const params: QueryInput = {
                    TableName: environment.AWS_DYNAMO_DB_TABLE_NAME,
                    IndexName: 'creatorCompanyId-projectStatus-index',
                    KeyConditionExpression: '#pKeyName = :pKeyValue and #sKeyName = :sKeyValue',
                    ExpressionAttributeNames: {
                        '#pKeyName': 'creatorCompanyId',
                        '#sKeyName': 'projectStatus',
                    },
                    ExpressionAttributeValues: {
                        ':pKeyValue': this.authSrv.authInfo.companyData._id,
                        ':sKeyValue': 'in-progress',
                    },
                };

                if (supportProjectListLastEvaluatedKey != null) {
                    params.ExclusiveStartKey = supportProjectListLastEvaluatedKey; // used to get the next results, after our previous query
                }

                const supportProjectListData = await this.db.dynamoDb.query(params).promise();

                if (supportProjectListData.LastEvaluatedKey != null) {
                    // we have not got all items, we need to query again
                    supportProjectListLastEvaluatedKey = supportProjectListData.LastEvaluatedKey;
                } else {
                    supportProjectListLastEvaluatedKey = null;
                    supportProjectListQueryComplete = true;
                }

                this.supportProjectList = supportProjectListData.Items.map((x) => x as MinimalProjectInfo);
                this.supportProjectList.sort((a, b) => a.name?.localeCompare(b.name));
                this.visibleSupportProjectList = this.supportProjectList;
                this.supportProjectLoading = false;
            } catch (err) {
                this.logger.error('[SYNC DOWNSTREAM] could not download project list for dokupit support team user', err);
                this.utils.sentryCaptureException(err);
            }
        }
    }

    async supportSearchChanged(event, supportProjectSearchbar) {
        this.visibleSupportProjectList = this.supportProjectList.filter((p) => {
            if (p.projectNumber != null) {
                if (p.projectNumber.indexOf(event.detail.value) != -1) {
                    return true;
                }
            }

            if (p.name.toLowerCase().indexOf(event.detail.value.toLowerCase()) != -1) {
                return true;
            }

            let addrStr = this.buildProjectAddressString(p.projectAddress);
            return addrStr.toLowerCase().indexOf(event.detail.value.toLowerCase()) != -1;
        });
    }

    /**
     * Downloads chosen project from the support-project-list
     * @private
     */
    async downloadSupportProject(projectId: string) {
        this.supportProjectLoading = true;
        await this.syncSrv.downloadProjectById(projectId);
        // start sync, so the project data will be synced
        await this.syncSrv.downloadItemsByRelation('project#' + projectId);
        this.supportProjectListModalVisible = false;
        this.supportProjectLoading = false;
    }

    /**
     * Build project address
     * @param address
     */
    buildProjectAddressString(address: Address) {
        return this.utils.buildProjectAddressString(address);
    }

    /**
     * Opens the barcode scanner
     */
    startScanSearch() {
        this.scanSearchEnabled = true;
    }

    /**
     * Triggered when the barcode scanner emits a result
     * @param scanResult
     */
    onScanResult(scanResult: string) {
        this.logger.log('[DASHBOARD] got scan result ', scanResult);
        this.jobSearchLoading = true;
        this.lastJobSearchString = scanResult;
        this.lastJobSearchResult = null;
        this.scanSearchEnabled = false;
        this.jobSearchModalVisible = true;
        this.projSrv.searchJobs(scanResult).subscribe(
            (searchRes) => {
                this.logger.log('[DASHBOARD] got search result for job search x', searchRes);
                this.lastJobSearchResult = searchRes;
                this.jobSearchLoading = false;
            },
            (searchErr) => {
                this.logger.error('[DASHBOARD] could not complete job search for scanResult ' + scanResult, searchErr);
                this.utils.sentryCaptureException(searchErr);
                this.jobSearchLoading = false;
            }
        );
    }

    /**
     * Changes the project sorting mode
     */
    async onChangeProjectSortingMode(newMode: 'name-a-z' | 'name-z-a' | 'project-number-1-9' | 'project-number-9-1') {
        await this.storage.set('dp_project_list_sorting_mode', newMode);
        this.loadInitialView();
    }

    /**
     * Sorts the current project list by name
     * @private
     */
    async sortProjectList(list: Project[] | ProjectFolder[]) {
        try {
            let sortMode = this.currentProjectListSortingMode;

            if (sortMode === 'project-number-1-9' || sortMode === 'project-number-9-1') {
                list.sort((a, b) => {
                    const isAProject = a.type === 'project';
                    const isBProject = b.type === 'project';
                    if (
                        (!isAProject && !isBProject) ||
                        ((a.projectNumber == null || a.projectNumber === '') && (b.projectNumber == null || b.projectNumber === ''))
                    ) {
                        // if both items we compare have no project numbers -> compare by project name
                        if (sortMode === 'project-number-9-1') {
                            return b.name.localeCompare(a.name);
                        } else {
                            return a.name.localeCompare(b.name);
                        }
                    } else {
                        let compareA;
                        let compareB;
                        if (!isAProject || a.projectNumber == null || a.projectNumber === '') {
                            compareA = '0';
                        } else {
                            compareA = a.projectNumber.toString();
                        }
                        if (!isBProject || b.projectNumber == null || b.projectNumber === '') {
                            compareB = '0';
                        } else {
                            compareB = b.projectNumber.toString();
                        }
                        // replace all chars except numbers for sorting
                        let aReplaced = parseFloat(compareA.replace(/[^0-9]/g, ''));
                        let bReplaced = parseFloat(compareB.replace(/[^0-9]/g, ''));
                        // check if the replaced values are valid numbers, then sort with these numbers
                        if (!isNaN(aReplaced) && !isNaN(bReplaced)) {
                            if (aReplaced == bReplaced) {
                                return 0;
                            } else if (aReplaced > bReplaced) {
                                if (sortMode === 'project-number-9-1') {
                                    return -1;
                                } else {
                                    return 1;
                                }
                            } else {
                                if (sortMode === 'project-number-9-1') {
                                    return 1;
                                } else {
                                    return -1;
                                }
                            }
                        } else {
                            if (compareA == compareB) {
                                return 0;
                            } else if (compareA > compareB) {
                                if (sortMode === 'project-number-9-1') {
                                    return -1;
                                } else {
                                    return 1;
                                }
                            } else {
                                if (sortMode === 'project-number-9-1') {
                                    return 1;
                                } else {
                                    return -1;
                                }
                            }
                        }
                    }
                });
            } else {
                list.sort((a, b) => {
                    if (sortMode === 'name-z-a') {
                        return b.name.localeCompare(a.name);
                    } else {
                        return a.name.localeCompare(b.name); // default sorting
                    }
                });
            }
        } catch (sortErr) {
            this.logger.error('[PROJECT SERVICE] failed to sort project list', sortErr);
        }
    }

    /**
     * Triggered when the user cancels the search results dialog
     */
    onCancelScanSearchResult() {
        this.jobSearchModalVisible = false;
        this.lastJobSearchResult = null;
        this.lastJobSearchString = null;
    }

    /**
     * Triggered when the user wants to start a new search from the search results dialog
     */
    onStartNewSearch() {
        this.jobSearchModalVisible = false;
        this.lastJobSearchResult = null;
        this.lastJobSearchString = null;
        this.startScanSearch();
    }

    /**
     * Triggered when the user selects a search result to navigate to
     * @param searchResult
     */
    async onGoToSearchResult(searchResult, jobSearchModal: IonModal) {
        await jobSearchModal.dismiss();
        this.router.navigate(['/project', searchResult.job.projectId, 'jobs'], { queryParams: { jobId: searchResult.job._id } });
    }

    /**
     * Archives Project
     * @param project
     */
    async onArchiveProject(project: Project) {
        await this.projSrv.setCurrentProject(project);
        try {
            let confirmed = await this.notifySrv.confirm(
                this.translateService.instant('projects.settings.info.archive-project-desc'),
                this.translateService.instant('projects.settings.info.archive-project-title'),
                this.translateService.instant('projects.settings.info.archive-btn')
            );
            this.projSrv.archiveCurrentProject();
        } catch (notConfirmed) {
            // confirmation declined
        }
    }

    /**
     * Deletes Project
     * @param project
     */
    async onDeleteProject(project: Project) {
        await this.projSrv.setCurrentProject(project);
        try {
            let confirmed = await this.notifySrv.confirm(
                this.translateService.instant('projects.settings.info.delete-project-desc'),
                this.translateService.instant('projects.settings.info.delete-project-title'),
                this.translateService.instant('shared.delete')
            );
            await this.projSrv.deleteCurrentProject();
        } catch (notConfirmed) {
            // confirmation declined
        }
    }

    async onStartDuplicatingProject(project: Project) {
        this.duplicateModalVisible = true;
        this.projectToDuplicate = project;
    }

    async startProjectCopyProcess() {
        // check if user is offline
        if (!this.utils.isOnline) {
            this.notifySrv.error(
                this.translateService.instant('projects.general.errors.no-network-desc'),
                this.translateService.instant('projects.general.errors.no-network-title')
            );
            this.duplicateModalVisible = false;
            return;
        }
        this.copyLoading = true;
        let skipJobs = !this.copyJobs;
        await this.http
            .put(
                `${environment.AWS_API_URL}/import-project`,
                {
                    sourceCompanyId: 'company#' + this.authSrv.authInfo.companyData._id,
                    company: 'company#' + this.authSrv.authInfo.companyData._id,
                    project: 'project#' + this.projectToDuplicate._id,
                    strictCopy: true,
                    copyDocuments: true,
                    copyGlobalServiceGroups: false,
                    executedBy: this.authSrv.authInfo.userData.email,
                    skipJobs: skipJobs,
                },
                {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                    }),
                }
            )
            .toPromise()
            .then((values) => {
                this.logger.log('[ENTERPRISE SETTINGS] copy project process finished', values);
                this.notifySrv.success(this.translateService.instant('settings.enterprise-settings.copy-project-successful'));
            })
            .catch((error) => {
                // do not throw error if error status is 504 => because api gateway timeout is 30 seconds and could not be increased
                if (error.status === 504) {
                    this.logger.warn('[ENTERPRISE SETTINGS] copy process not finished yet => time out because of api gateway', error);
                    this.notifySrv.warn(this.translateService.instant('settings.enterprise-settings.errors.timeout-warning'));
                } else {
                    this.notifySrv.error(this.translateService.instant('settings.enterprise-settings.errors.copy-error'));
                    this.logger.error('[ENTERPRISE SETTINGS] copy process finished with an error', error);
                }
            });
        this.copyJobs = false;
        this.copyLoading = false;
        this.duplicateModalVisible = false;
    }

    private updateCombinedList() {
        this.combinedListItems = [...this.currentVisibleProjectFolders, ...this.currentVisibleProjects];
    }

    protected readonly possibleProjectStatus = ProjectStatus;
}
